/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useMemo, useRef, useState } from 'react';
import { StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { PaperSheet } from '../../shared/ticket/PaperSheet'
import { TicketKpiStrip } from '../../shared/ticket/TicketKpiStrip'
import type { TicketKpiItem } from '../../shared/ticket/TicketKpiStrip'
import { TicketModuleHeader } from '../../shared/ticket/TicketModuleHeader';
import { ticketColors } from '../../shared/theme/ticketColors';
import { OperationalFilterSearchField } from '../../shared/filters/OperationalFilterPrimitives'
import { useOperationalFilterLayout } from '../../shared/filters/useOperationalFilterLayout';
import { useOperationalWebSearchFocus } from '../../shared/ui/hooks-screen/useOperationalWebSearchFocus';
import { formatMoneyCompact } from '../../shared/format/moneyFormat';
import type { SaleListRow } from '../domain/salesTypes';
import type { SalesListKpis } from '../screen-hooks/useSalesListKpis';

type Props = {
  rows: SaleListRow[];
  loading: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  onExportCsv: () => void;
  canExport?: boolean;
  hideSummary?: boolean;
  serverKpis?: SalesListKpis | null;
  filterTrigger?: React.ReactNode;
  /** Chip de período visible (abre el picker de fechas). */
  periodTrigger?: React.ReactNode;
  /** Trigger "Más" (sheet de acciones secundarias). */
  moreTrigger?: React.ReactNode;
  /** KPI "Pendiente" como toggle del filtro de cobro pendiente. */
  pendingKpiSelected?: boolean;
  onPendingKpiPress?: () => void;
  /** KPI "Anuladas" como toggle del filtro de estado. */
  voidedKpiSelected?: boolean;
  onVoidedKpiPress?: () => void;
};

type HeaderSubProps = Props & { searchInputRef: React.RefObject<TextInput | null> };

function useSalesKpis(rows: SaleListRow[]) {
  return useMemo(() => {
    let totalSum = 0;
    let paidSum = 0;
    let pendingSum = 0;
    let voidedCount = 0;
    let activeCount = 0;
    for (const r of rows) {
      if (r.status === 'voided') {
        voidedCount++;
        continue;
      }
      activeCount++;
      totalSum += Number(r.total ?? 0);
      paidSum += Number(r.paidAmount ?? 0);
      pendingSum += Number(r.pendingAmount ?? 0);
    }
    const ticketPromedio = activeCount > 0 ? totalSum / activeCount : 0;
    return { totalSum, paidSum, pendingSum, voidedCount, activeCount, ticketPromedio };
  }, [rows]);
}

type KpiActions = {
  pendingKpiSelected?: boolean;
  onPendingKpiPress?: () => void;
  voidedKpiSelected?: boolean;
  onVoidedKpiPress?: () => void;
};

function buildKpiItems(
  kpis: ReturnType<typeof useSalesKpis>,
  options?: { compact?: boolean },
  actions?: KpiActions
): TicketKpiItem[] {
  const items: TicketKpiItem[] = [
    { label: 'Total del período', value: formatMoneyCompact(kpis.totalSum) },
    { label: 'Cobrado', value: formatMoneyCompact(kpis.paidSum), tone: 'ok' },
    {
      label: 'Pendiente',
      value: formatMoneyCompact(kpis.pendingSum),
      tone: kpis.pendingSum > 0 ? 'warn' : 'default',
      onPress: actions?.onPendingKpiPress,
      selected: actions?.pendingKpiSelected,
      testID: 'ventas.kpi.pendiente',
      accessibilityLabel: `Pendiente de cobro: ${formatMoneyCompact(kpis.pendingSum)}. Filtra las pendientes.`,
    },
  ];
  if (!options?.compact) {
    items.push({
      label: 'Ticket promedio',
      value: kpis.activeCount > 0 ? formatMoneyCompact(kpis.ticketPromedio) : '—',
    });
  }
  items.push({ label: 'Ventas', value: String(kpis.activeCount) });
  // Silencio por defecto: aparece solo si hay anuladas o si el filtro está ON
  // (para poder apagarlo desde la misma tile).
  if (kpis.voidedCount > 0 || actions?.voidedKpiSelected) {
    items.push({
      label: 'Anuladas',
      value: String(kpis.voidedCount),
      tone: 'bad',
      onPress: actions?.onVoidedKpiPress,
      selected: actions?.voidedKpiSelected,
      testID: 'ventas.kpi.anuladas',
      accessibilityLabel: `Ventas anuladas: ${kpis.voidedCount}. Filtra las anuladas.`,
    });
  }
  return items;
}

/**
 * La tira se muestra siempre (proto ventas-ux-v1): los ceros del período
 * vacío son información — "Total $0 · 0 ventas" — no ruido. Solo se
 * omite si el caller pide `hideSummary` (reuso embebido).
 */
function shouldHideKpis(hideSummary: boolean): boolean {
  return hideSummary;
}

export function SalesScreenHeader(props: Props) {
  const { layoutWide } = useOperationalFilterLayout();
  const searchInputRef = useRef<TextInput>(null);
  useOperationalWebSearchFocus(searchInputRef, {
    shortcutsEnabled: true,
  });

  const subProps: HeaderSubProps = { ...props, searchInputRef };

  if (layoutWide) return <WideHeader {...subProps} />;
  return <MobileHeader {...subProps} />;
}

function DeskActionTile({
  label,
  onPress,
  disabled,
  testID,
  accessibilityLabel,
}: {
  label: string;
  onPress: () => void;
  disabled?: boolean;
  testID: string;
  accessibilityLabel: string;
}) {
  return (
    <TouchableOpacity
      style={[styles.deskTile, disabled && { opacity: 0.45 }]}
      onPress={onPress}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityState={{ disabled }}
      testID={testID}
    >
      <Text style={styles.deskTileText}>{label}</Text>
    </TouchableOpacity>
  );
}

function WideHeader({
  rows,
  search,
  onSearchChange,
  onExportCsv,
  canExport = true,
  hideSummary = false,
  searchInputRef,
  serverKpis = null,
  filterTrigger,
  periodTrigger,
  moreTrigger,
  pendingKpiSelected,
  onPendingKpiPress,
  voidedKpiSelected,
  onVoidedKpiPress,
}: HeaderSubProps) {
  const fallback = useSalesKpis(rows);
  const kpis = serverKpis ?? fallback;
  const kpiActions: KpiActions = {
    pendingKpiSelected,
    onPendingKpiPress,
    voidedKpiSelected,
    onVoidedKpiPress,
  };

  return (
    <View style={{ gap: 10 }}>
      <TicketModuleHeader
        kicker="Ventas"
        title="Ventas del período"
        meta="Historial de ventas de mostrador y pedidos online"
        testID="ventas.header.sheet"
        tools={
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <View style={{ flex: 1, minWidth: 220 }}>
              <OperationalFilterSearchField
                inputRef={searchInputRef}
                value={search}
                onChangeText={onSearchChange}
                placeholder="Buscar por referencia, monto o producto"
                appearance="desk"
                accessibilityLabel="Buscar ventas"
                testID="ventas.search"
              />
            </View>
            {periodTrigger}
            {filterTrigger}
            {moreTrigger}
            {canExport ? (
              <DeskActionTile
                label="Exportar CSV"
                onPress={onExportCsv}
                testID="ventas.export-csv"
                accessibilityLabel="Exportar CSV"
              />
            ) : null}
          </View>
        }
      />

      {!shouldHideKpis(hideSummary) ? (
        <PaperSheet testID="ventas.kpi.sheet">
          <TicketKpiStrip items={buildKpiItems(kpis, undefined, kpiActions)} layout="desk" variant="soft" />
          {serverKpis == null ? (
            <Text style={styles.kpiFallbackNote}>Del período visible en pantalla</Text>
          ) : null}
        </PaperSheet>
      ) : null}
    </View>
  );
}

function MobileHeader({
  rows,
  search,
  onSearchChange,
  onExportCsv,
  canExport = true,
  hideSummary = false,
  searchInputRef,
  serverKpis = null,
  filterTrigger,
  periodTrigger,
  moreTrigger,
  pendingKpiSelected,
  onPendingKpiPress,
  voidedKpiSelected,
  onVoidedKpiPress,
}: HeaderSubProps) {
  const fallback = useSalesKpis(rows);
  const kpis = serverKpis ?? fallback;
  const kpiActions: KpiActions = {
    pendingKpiSelected,
    onPendingKpiPress,
    voidedKpiSelected,
    onVoidedKpiPress,
  };

  return (
    <View style={{ gap: 10 }}>
      <TicketModuleHeader
        kicker="Ventas"
        title="Ventas del período"
        meta="Historial de ventas de mostrador y pedidos online"
        testID="ventas.header.sheet"
        tools={
          <>
            <View style={styles.searchBoxMobile}>
              <Ionicons name="search-outline" size={18} color={ticketColors.ink3} />
              <TextInput
                ref={searchInputRef}
                value={search}
                onChangeText={onSearchChange}
                placeholder="Buscar ventas"
                placeholderTextColor={ticketColors.ink3}
                style={styles.searchInputMobile}
                accessibilityLabel="Buscar ventas"
                autoCorrect={false}
                returnKeyType="search"
                blurOnSubmit={false}
                testID="ventas.search"
              />
              {search.trim().length > 0 ? (
                <TouchableOpacity
                  onPress={() => onSearchChange('')}
                  accessibilityRole="button"
                  accessibilityLabel="Limpiar búsqueda"
                >
                  <Ionicons name="close-circle" size={20} color={ticketColors.ink2} />
                </TouchableOpacity>
              ) : null}
            </View>
            <View style={styles.toolsRowMobile}>
              {periodTrigger}
              {filterTrigger}
              {moreTrigger}
              {canExport ? (
                <TouchableOpacity
                  style={styles.mobileExportBtn}
                  onPress={onExportCsv}
                  activeOpacity={0.7}
                  accessibilityRole="button"
                  accessibilityLabel="Exportar CSV"
                  testID="ventas.export-csv"
                >
                  <Ionicons name="download-outline" size={18} color={ticketColors.ink2} />
                </TouchableOpacity>
              ) : null}
            </View>
          </>
        }
      />

      {!shouldHideKpis(hideSummary) ? (
        <PaperSheet testID="ventas.kpi.sheet">
          <TicketKpiStrip
            items={buildKpiItems(kpis, { compact: true }, kpiActions)}
            layout="mobile"
            variant="soft"
          />
          {serverKpis == null ? (
            <Text style={styles.kpiFallbackNote}>Del período visible en pantalla</Text>
          ) : null}
        </PaperSheet>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  // ── KPI fallback (snapshot RPC caído): aclaración chica, mismo label. ──
  kpiFallbackNote: {
    fontSize: 10.5,
    color: ticketColors.ink3,
    marginTop: 8,
  },
  // ── Desk tile (h42 / r10) ──
  deskTile: {
    height: 42,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.tile,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 14,
  },
  deskTileText: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  // ── Mobile search ──
  searchBoxMobile: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    height: 42,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.tile,
    paddingHorizontal: 12,
  },
  searchInputMobile: {
    flex: 1,
    fontSize: 15,
    color: ticketColors.ink,
    paddingVertical: 0,
  },
  toolsRowMobile: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  // ── Mobile export (icon-only 42×42) ──
  mobileExportBtn: {
    width: 42,
    height: 42,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.tile,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
